import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import Checkbox from '@mui/material/Checkbox';
import { RoomType } from 'providers/RoomRegistrar';
import { useActiveEditor } from '../../ActiveEditorContext';
import { useMultiEditorComments } from '../../hooks/useMultiEditorComments';
import '@liveblocks/react-ui/styles.css';
import { PyThreadInputContainer, PyThreadInputHeader, PyCheckboxContainer, PyCheckboxLabel, PyThreadInputMenuContainer } from './styled';
import { CustomComposer } from './CustomComposer';
import type { ComposerSubmitComment } from '@liveblocks/react-ui/primitives';
import type { CommentBody } from '@liveblocks/client';
import { useComments } from 'providers/CommentsContext';
import { ThreadPrivateAuthor, ThreadPublicAuthor } from './ThreadAuthor';
import { openNotification } from 'components/notification';
import type { MenuPosition } from './threadInputHelpers';
import { updateMenuPosition } from './threadInputHelpers';

export const ThreadInput = ({ threadPanelRef, contentId }: { threadPanelRef: React.RefObject<HTMLElement>; contentId: string }) => {
  const { activeEditor } = useActiveEditor();
  const { createThread } = useMultiEditorComments(contentId);
  const { t } = useTranslation();
  const { userType: contextUserType, commentsEnabled } = useComments();
  const [userType, setUserType] = useState<RoomType>(contextUserType);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const selectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCancelCreateThread = useCallback(() => {
    if (activeEditor) {
      activeEditor.commands.clearSelection();
      activeEditor.commands.setTextSelection(0);
    }

    const browserSelection = window.getSelection();
    if (browserSelection && browserSelection.rangeCount > 0) {
      browserSelection.removeAllRanges();
    }

    setShowMenu(false);
    setMenuPosition(null);
  }, [activeEditor]);

  const handleCreateThread = useCallback(
    async (text: CommentBody, roomType: RoomType) => {
      const result = await createThread(text, roomType);

      if (result?.success) {
        handleCancelCreateThread();
      } else if (result?.error === 'maxThreadsReached') {
        openNotification({
          title: t('comments.maxThreadsReachedTitle'),
          description: t('comments.maxThreadsReached'),
          type: 'info',
          placement: 'bottomRight',
        });
      } else if (result?.error === 'identifyViewer') {
        handleCancelCreateThread();
      }
    },
    [createThread, handleCancelCreateThread]
  );

  const handleComposerSubmit = useCallback(
    async (comment: ComposerSubmitComment) => {
      if (comment.body) {
        await handleCreateThread(comment.body, userType);
      }
    },
    [handleCreateThread, userType]
  );

  useEffect(() => {
    if (!activeEditor || !commentsEnabled) {
      setShowMenu(false);
      return;
    }

    const handleSelectionUpdate = () => {
      const { from, to } = activeEditor.state.selection;

      if (from === to) {
        setShowMenu(false);
        if (selectionTimeoutRef.current) {
          clearTimeout(selectionTimeoutRef.current);
          selectionTimeoutRef.current = null;
        }
        return;
      }

      const browserSelection = window.getSelection();
      const isSelecting = (browserSelection?.rangeCount ?? 0) > 0 && !browserSelection?.isCollapsed;

      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }

      if (isSelecting) {
        setShowMenu(false);
        selectionTimeoutRef.current = setTimeout(() => {
          if (activeEditor.state.selection.from !== activeEditor.state.selection.to) {
            updateMenuPosition(activeEditor, commentsEnabled, setShowMenu, threadPanelRef, setMenuPosition);
          }
        }, 500);
      } else {
        updateMenuPosition(activeEditor, commentsEnabled, setShowMenu, threadPanelRef, setMenuPosition);
      }
    };

    const checkCurrentSelection = () => {
      const { from, to } = activeEditor.state.selection;
      if (from !== to) {
        handleSelectionUpdate();
      }
    };

    activeEditor.on('selectionUpdate', handleSelectionUpdate);
    checkCurrentSelection();

    return () => {
      activeEditor.off('selectionUpdate', handleSelectionUpdate);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [activeEditor, commentsEnabled, threadPanelRef]);

  useEffect(() => {
    if (!commentsEnabled) {
      setShowMenu(false);
    }
  }, [commentsEnabled]);

  if (!activeEditor || !showMenu || !menuPosition) return null;

  const container = threadPanelRef.current || document.body;

  const menuContent = (
    <PyThreadInputMenuContainer top={menuPosition.top} left={menuPosition.left}>
      <PyThreadInputContainer>
        <PyThreadInputHeader>
          {contextUserType === RoomType.PRIVATE ? <ThreadPrivateAuthor /> : <ThreadPublicAuthor />}

          {contextUserType === RoomType.PRIVATE && (
            <PyCheckboxContainer>
              <PyCheckboxLabel>{t('comments.privateComment')}</PyCheckboxLabel>
              <Checkbox
                checked={userType === RoomType.PRIVATE}
                onChange={() => setUserType((p) => (p === RoomType.PRIVATE ? RoomType.PUBLIC : RoomType.PRIVATE))}
              />
            </PyCheckboxContainer>
          )}
        </PyThreadInputHeader>
        <CustomComposer
          roomType={userType}
          onCancel={handleCancelCreateThread}
          onSubmit={handleComposerSubmit}
          placeholder={t('comments.enterComment')}
          submitLabel={t('comments.addComment')}
          cancelLabel={t('comments.cancel')}
        />
      </PyThreadInputContainer>
    </PyThreadInputMenuContainer>
  );

  return createPortal(menuContent, container);
};
